/**
 * CortexFlagBanner — C4.3 (era ShadowModeBanner)
 *
 * C4.3 (2026-04): Cortex flags são binárias. Não há mais "shadow", "canary"
 * ou rollout parcial. Este banner apenas avisa quando alguma flag relevante
 * da Fase 3 está DESLIGADA, para o operador entender por que uma métrica
 * pode estar zerada.
 */
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, EyeOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const PHASE3_FLAGS = [
  "feature.wm_v18_tables",
  "feature.invocation_window",
  "feature.interruption_classifier",
  "feature.wm_composer_shadow",
  "feature.tom_v2",
  "feature.tom_turn_inference",
  "feature.tom_directives",
  "feature.persona_v19",
  "feature.persona_modulator",
  "feature.persona_drift_detector",
  "feature.prompt_composition_trace",
  "feature.person_memory_v19",
  "feature.skill_os_v19",
  "feature.multimodal_v19",
] as const;

type FlagRow = { flag_key: string; enabled: boolean };

interface ShadowModeBannerProps {
  context?: string;
  compact?: boolean;
}

export function ShadowModeBanner({ context, compact = false }: ShadowModeBannerProps) {
  const { data, isLoading } = useQuery({
    queryKey: ["phase3-flag-status"],
    staleTime: 60_000,
    queryFn: async (): Promise<FlagRow[]> => {
      const { data } = await supabase
        .from("cortex_feature_flags")
        .select("flag_key, enabled")
        .in("flag_key", PHASE3_FLAGS as unknown as string[]);
      return (data as FlagRow[]) ?? [];
    },
  });

  if (isLoading || !data || data.length === 0) return null;

  const off = data.filter((f) => !f.enabled);
  const on = data.filter((f) => f.enabled);

  if (off.length === 0) return null;

  return (
    <Card className="border-warning/40 bg-warning/5">
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-warning mt-0.5 shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <strong className="text-warning">Fase 3 com recursos desligados</strong>
              <Badge variant="outline" className="text-xs gap-1">
                <EyeOff className="w-3 h-3" /> {off.length} off
              </Badge>
              {on.length > 0 && (
                <Badge variant="default" className="text-xs">{on.length} on</Badge>
              )}
            </div>
            <p className="text-sm text-foreground">
              Recursos OFF não geram telemetria — métricas zeradas dessas flags são <strong>esperadas</strong>.
              {context ? ` ${context}` : ""}
            </p>
            {!compact && off.length > 0 && (
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer hover:text-foreground">Detalhes por flag</summary>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {off
                    .sort((a, b) => a.flag_key.localeCompare(b.flag_key))
                    .map((f) => (
                      <div key={f.flag_key} className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-muted/40">
                        <code className="truncate">{f.flag_key.replace("feature.", "")}</code>
                        <Badge variant="outline" className="text-[10px] shrink-0">off</Badge>
                      </div>
                    ))}
                </div>
              </details>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** Badge inline para indicar que uma flag específica está OFF. */
export function ShadowMetricBadge({ flagKey }: { flagKey: string }) {
  const { data } = useQuery({
    queryKey: ["phase3-flag-status"],
    staleTime: 60_000,
    queryFn: async (): Promise<FlagRow[]> => {
      const { data } = await supabase
        .from("cortex_feature_flags")
        .select("flag_key, enabled")
        .in("flag_key", PHASE3_FLAGS as unknown as string[]);
      return (data as FlagRow[]) ?? [];
    },
  });

  const flag = data?.find((f) => f.flag_key === flagKey);
  if (!flag || flag.enabled) return null;

  return (
    <Badge variant="outline" className="text-[10px] gap-1">
      <EyeOff className="w-2.5 h-2.5" /> off
    </Badge>
  );
}
